import { useState, useRef } from "react";
import cloneDeep from "lodash/cloneDeep";

type ValidateFormType = {
  callback?: () => void;
  controlsToTouched?: boolean;
  controlsToDirty?: boolean;
};

/**
 * @deprecated
 */
// TODO: вместо него юзать react-hook-form
export const useFormController = (initControls, { onChange }) => {
  const _initControls = useRef(initControls);
  const formSubmitted = useRef(false);
  const [controls, setControls] = useState(initControls);

  const getControls = (typeControl?: string) => {
    return controls.filter(control => {
      if (typeControl) {
        return control && control.typeControl === typeControl;
      }
      return !!control;
    });
  };

  const getError = (id: string) => {
    const valid = validateControl(id);
    return typeof valid === "boolean" ? "" : valid;
  };

  const getControl = controlID => {
    return getControls().find(control => {
      return control.id === controlID;
    });
  };

  const getValue = controlID => {
    const control = getControl(controlID);
    if (control) {
      return getControl(controlID)?.value;
    }
    return null;
  };

  const addControl = control => {
    setControls(controls => {
      const newControls = cloneDeep(controls);
      newControls.push(control);
      return newControls;
    });
  };

  const removeControl = id => {
    const controls = getControls().filter(control => {
      return control.id !== id;
    });
    setControls(controls);
  };

  const validateControl = (id: string, skipTouched?: boolean) => {
    const control = getControl(id);
    if (!control) {
      return;
    }
    if (!control.isTouched && !skipTouched) {
      return true;
    }
    if (control.rules) {
      const errorRule = control.rules.find(rule => {
        if (!rule.validator(control.value)) {
          return true;
        }
        return false;
      });
      return errorRule ? errorRule.message() : true;
    }
    if (control.rulesItem) {
      const errorRule = control.rulesItem.find(rule => {
        if (!rule.validator(control.selectedItem)) {
          return true;
        }
        return false;
      });
      return errorRule ? errorRule.message() : true;
    }
    return true;
  };

  const validateForm = async ({
    callback,
    controlsToTouched,
    controlsToDirty,
  }: ValidateFormType | undefined) => {
    const controls = getControls();
    const firstErrorMessage = controls.find(control => {
      return typeof validateControl(control.id, true) === "string";
    });
    if (controlsToTouched) {
      setControlsTouched(true, controlsToDirty);
    }
    if (!firstErrorMessage) {
      if (typeof callback === "function") {
        await callback();
      }
    }
    return !firstErrorMessage;
  };

  const setValue = (value, id, isTouched) => {
    const controls = getControls();
    const newControls = controls.map(control => {
      if (control.id === id) {
        const cloneControl = cloneDeep(control);
        cloneControl.value = value;
        if (formSubmitted.current || isTouched) {
          cloneControl.isTouched = true;
        }
        return cloneControl;
      }
      return control;
    });
    if (typeof onChange === "function") {
      onChange();
    }
    setControls(newControls);
  };

  const setItem = (item, id) => {
    const controls = getControls();
    const newControls = controls.map(control => {
      if (control.id === id) {
        const cloneControl = cloneDeep(control);
        cloneControl.selectedItem = item;
        if (formSubmitted.current) {
          cloneControl.isTouched = true;
        }
        return cloneControl;
      }
      return control;
    });
    if (typeof onChange === "function") {
      onChange();
    }
    setControls(newControls);
  };

  const setControlsTouched = (
    touched: boolean,
    setControlToDirty?: boolean
  ) => {
    setControls(
      getControls().map(control => {
        const cloneControl = cloneDeep(control);
        cloneControl.isTouched = touched;
        if (setControlToDirty) {
          cloneControl.isDirty = true;
        }
        return cloneControl;
      })
    );
  };

  const reset = () => {
    setControlsTouched(false);
    formSubmitted.current = false;
    setControls(_initControls.current);
    if (typeof onChange === "function") {
      onChange();
    }
  };

  const resetValidationForm = () => {
    formSubmitted.current = false;
    setControls(
      getControls().map(control => {
        const cloneControl = cloneDeep(control);
        cloneControl.isTouched = false;
        return cloneControl;
      })
    );
  };

  const setDirty = (isDirty, id) => {
    const controls = getControls();
    const newControls = controls.map(control => {
      if (control.id === id) {
        const cloneControl = cloneDeep(control);
        cloneControl.isDirty = isDirty;
        return cloneControl;
      }
      return control;
    });
    setControls(newControls);
  };

  const onChangeValue = (
    value:
      | string
      | null
      | number
      | Record<string, unknown>
      | boolean
      | undefined,
    id: string,
    isTouched?: boolean
  ) => {
    setValue(value, id, isTouched);
  };

  const onChangeItem = (item, id) => {
    setItem(item, id);
  };

  const submitForm = async callback => {
    formSubmitted.current = true;
    await validateForm({
      controlsToTouched: true,
      controlsToDirty: true,
      callback,
    });
  };

  return {
    addControl,
    removeControl,
    getControls,
    getControl,
    getValue,
    setValue,
    setItem,
    validateForm,
    getError,
    reset,
    resetValidationForm,
    setDirty,
    onChangeValue,
    onChangeItem,
    submitForm,
  };
};
